import { jwtDecode } from 'jwt-decode';
import {
    CognitoUserPool,
    CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { clearAuthCache } from './auth-cache';
import { environment } from './amplify';

interface JWTPayload {
    exp: number;
    [key: string]: any;
}

/**
 * Renews the session from the stored refresh token. This calls the Cognito
 * client directly rather than Amplify Auth, because Amplify answers a revoked
 * or expired refresh token by redirecting to the global sign-out page.
 *
 * @returns Promise<boolean> - true if the stored session is now valid
 */
const refreshSession = async (): Promise<boolean> => {
    try {
        const { USER_POOL_ID, APP_CLIENT_ID } = await environment();
        const user = new CognitoUserPool({
            UserPoolId: USER_POOL_ID,
            ClientId: APP_CLIENT_ID,
        }).getCurrentUser();
        if (!user) return false;

        // getSession() refreshes and stores new tokens when the cached ones have expired
        await new Promise<CognitoUserSession>((resolve, reject) =>
            user.getSession(
                (error: Error | null, session: CognitoUserSession | null) =>
                    error || !session ? reject(error) : resolve(session),
            ),
        );
        return true;
    } catch (error) {
        return false;
    }
};

/**
 * Proactively checks if Cognito access tokens are expired. If they are, renews them with the
 * refresh token, and cleans up localStorage if that fails.
 * This prevents Amplify from detecting stale auth state and redirecting to logout instead of login.
 *
 * Based on Event Horizon team's approach from EventHorizon-4696 and CR-212031397.
 *
 * @returns Promise<boolean> - true if tokens were expired and cleaned up, false otherwise
 */
export const checkAndCleanupExpiredTokens = async (): Promise<boolean> => {
    try {
        // Look for Cognito access tokens in localStorage
        const cognitoKeys = Object.keys(localStorage).filter(
            (key) =>
                key.includes('CognitoIdentityServiceProvider') &&
                key.endsWith('.accessToken'),
        );

        if (cognitoKeys.length === 0) {
            // No access tokens found, nothing to check
            return false;
        }

        let hasExpiredTokens = false;

        // Check each access token for expiration
        for (const key of cognitoKeys) {
            const token = localStorage.getItem(key);
            if (!token) continue;

            try {
                const decoded = jwtDecode<JWTPayload>(token);
                const currentTime = Math.floor(Date.now() / 1000);

                // Check if token is expired (with 30 second buffer)
                if (decoded.exp && decoded.exp <= currentTime + 30) {
                    hasExpiredTokens = true;
                    break;
                }
            } catch (decodeError) {
                // If we can't decode the token, consider it invalid/expired
                hasExpiredTokens = true;
                break;
            }
        }

        if (hasExpiredTokens) {
            if (await refreshSession()) {
                return false;
            }
            // Refresh failed: clear all auth cache to prevent logout redirect
            clearAuthCache();
            return true;
        }

        return false;
    } catch (error) {
        // If there's any error in the process, clear cache as a safety measure
        clearAuthCache();
        return true;
    }
};

/**
 * Gets the expiration time of the current access token if available
 * @returns number | null - Unix timestamp of token expiration, or null if no valid token
 */
export const getTokenExpirationTime = (): number | null => {
    try {
        const cognitoKeys = Object.keys(localStorage).filter(
            (key) =>
                key.includes('CognitoIdentityServiceProvider') &&
                key.endsWith('.accessToken'),
        );

        if (cognitoKeys.length === 0) {
            return null;
        }

        // Get the first access token (there should typically be only one)
        const token = localStorage.getItem(cognitoKeys[0]);
        if (!token) return null;

        const decoded = jwtDecode<JWTPayload>(token);
        return decoded.exp || null;
    } catch (error) {
        return null;
    }
};

/**
 * Checks if the current access token is expired
 * @param bufferSeconds - Number of seconds before expiry to consider token expired (default: 30)
 * @returns boolean - true if token is expired or will expire within buffer time
 */
export const isTokenExpired = (bufferSeconds: number = 30): boolean => {
    const expirationTime = getTokenExpirationTime();
    if (!expirationTime) return true;

    const currentTime = Math.floor(Date.now() / 1000);
    return expirationTime <= currentTime + bufferSeconds;
};
